import { useEffect, useState } from "react";
import { MdAdd, MdRefresh } from "react-icons/md";
// import { useAccount } from "../../context/AccountContext";
import RulesTable from "../tables/RulesTable";
import RuleModal from "../modals/RuleModal";
import { listRules, createRule, updateRule, deleteRule, createDefaultRules } from "../api/rule";
import { useSelector } from "react-redux";

export default function Rules() {
  // const { entityId, loading: accountLoading } = useAccount();
  const {accountId, entityId } = useSelector((state) => state.account);
  // State variables. These will hold the rules, modal state, loading state, and error messages.
  const [rules, setRules] = useState([]);
  const [modal, setModal] = useState({ open: false, initial: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Update the refresh function to use entityId instead of entity_id
  // This function is called when the component mounts and can also be called manually to refresh the data. 
  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("RulesPage: Refreshing rules for accountId:", accountId);
      const rulesData = await listRules(accountId); // Updated to use accountId
      setRules(rulesData);
    } catch (err) {
      setError("Failed to load rules. Please try again.");
      console.error("Error loading rules:", err);
    } finally {
      setLoading(false);
    }
  };
  // Initial data fetch when the component mounts
  // This useEffect hook runs once when the component is first rendered to fetch the initial data.
  useEffect(() => {
    if (accountId) {
      refresh();
    }
  }, [accountId]); // Updated dependency

  

  // Function to handle saving a rule. It checks if the modal has an initial value (for editing) or not (for creating).
  // If the modal has an initial value, it updates the existing rule; otherwise, it creates a new rule.
  // After saving, it closes the modal and refreshes the rules list.
  const handleSave = async (data) => {
    // If no data is provided, close the modal without saving
    if (!data) {
      setModal({ open: false, initial: null });
      return;
    }
    // If the data is empty, show an error and return.
    // This prevents saving empty rules.
    try {
      // Validate the data before saving
      setLoading(true);
      // Check if the label is provided
      if (modal.initial) {
        // If the modal has an initial value, update the existing rule
        await updateRule(modal.initial.id, data);
      } else {
        // If the modal does not have an initial value, create a new rule
        await createRule(data);
      }
      setModal({ open: false, initial: null });
      await refresh();
    } catch (err) {
      setError(`Failed to ${modal.initial ? "update" : "create"} rule`);
      console.error("Error saving rule:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this rule?")) return;

    try {
      setLoading(true);
      await deleteRule(id);
      await refresh();
    } catch (err) {
      setError("Failed to delete rule");
      console.error("Error deleting rule:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUseDefaultRules = async () => {
    try {
      setLoading(true);
      setError(null);
      await createDefaultRules(accountId);
      await refresh(); // Refresh the list after creating defaults
    } catch (err) {
      console.error("Error creating default rules:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white border border-gray-200 rounded-md shadow-sm p-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Rules</h1>
            {!loading && (
              <p className="text-sm text-gray-500 mt-1">
                {rules.length} rule{rules.length !== 1 ? "s" : ""} total
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={refresh}
              disabled={loading}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
              title="Refresh"
            >
              <MdRefresh className={`${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              className="inline-flex items-center gap-1 px-4 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              onClick={() => setModal({ open: true, initial: null })}
              disabled={loading}
            >
              <MdAdd />
              New Rule
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 text-sm text-red-600 bg-red-50 rounded-md border border-red-100">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && !rules.length ? (
          <div className="text-center py-12 text-gray-500">Loading rules...</div>
        ) : !rules.length ? (
          <div className="text-center py-4">
            <div className="max-w-md mx-auto">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">
                No Rules Set Up Yet
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                Get started by using our default rules or create your own custom rules.
              </p>

              <div className="mb-4 text-left bg-gray-50 p-3 rounded-lg">
                <div className="text-[11px] font-medium text-gray-600 mb-2">Common examples:</div>
                <ul className="space-y-1.5 text-[11px] text-gray-600 list-inside">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-red-200 rounded-full shrink-0 mt-1.5"></span>
                    <div>
                      <span className="font-medium text-gray-700">Speeding Violation</span>
                      <span className="text-gray-500"> • -15 points • 3 year period</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-red-200 rounded-full shrink-0 mt-1.5"></span>
                    <div>
                      <span className="font-medium text-gray-700">DUI Offense</span>
                      <span className="text-gray-500"> • -50 points • 5 year period</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-green-200 rounded-full shrink-0 mt-1.5"></span>
                    <div>
                      <span className="font-medium text-gray-700">Clean Record Bonus</span>
                      <span className="text-gray-500"> • +10 points • 1 year clean record</span>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="flex gap-2 justify-center">
                <button
                  onClick={() => handleUseDefaultRules()}
                  className="px-2.5 py-1 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors"
                >
                  Use Default Rules
                </button>
                <span className="text-[11px] text-gray-400 flex items-center">or</span>
                <button
                  onClick={() => setModal({ open: true, initial: null })}
                  className="px-2.5 py-1 border border-gray-200 text-gray-600 text-xs font-medium rounded-md hover:bg-gray-50 transition-colors"
                >
                  Create Custom Rule
                </button>
              </div>
            </div>
          </div>
        ) : (
          <RulesTable
            rules={rules}
            onEdit={(rule) => setModal({ open: true, initial: rule })}
            onDelete={handleDelete}
          />
        )}

        <RuleModal
          open={modal.open}
          initial={modal.initial}
          account_id={accountId}
          onClose={handleSave}
          isLoading={loading}
        />
      </div>
    </div>
  );
}